import { ContextMenuWrapper } from "components/ContextMenu/ContextMenu";
import { LazyResizeableCodeEditor as ResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { SimpleToggle } from "components/Form/components/Switch/Switch";
import RecordLabel from "components/RecordLabel";
import { TableColumn } from "components/Table/tableUtils";
import { getUrlDTO } from "config/resources/sharedFields";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import { Schedule } from "graphql/generated/graphql";
import { getRelativeUrl } from "utils/service";
import { serializeToPrettyYaml } from "utils/valueTransformers";

export const buildSimpleColumn = (content, setNewValue?: () => void) => {
    if (setNewValue) {
        return <ContextMenuWrapper handleClick={setNewValue}>{content}</ContextMenuWrapper>;
    }
    return content;
};

export type AdditionalColumn = (
    setNewValue?: (data: { field: string; value: any }) => void
) => TableColumn;

export const gitEnabledColumn: AdditionalColumn = () => ({
    label: "Git enabled",
    key: "git_enabled",
    shown: false,
    sortable: false,
    dto: (value: any) => (
        <SimpleToggle identifier="git_enabled" value={!!value?.branch_name} isReadOnly={true} />
    ),
});
export const branchNameColumn: AdditionalColumn = (setNewValue) => ({
    label: "Branch name",
    key: "branch_name",
    shown: false,
    sortable: false,
    dto: (value: any) =>
        buildSimpleColumn(
            value?.branch_name,
            setNewValue
                ? () =>
                      setNewValue({
                          field: "branch_name",
                          value: value.branch_name,
                      })
                : undefined
        ),
});
export const gitUrlColumn: AdditionalColumn = (setNewValue) => ({
    label: "Git url",
    key: "git_url",
    shown: false,
    sortable: false,
    dto: (value: any) => {
        const content = value?.branch_name ? getUrlDTO(value.git_url) : null;
        return buildSimpleColumn(
            content,
            setNewValue
                ? () =>
                      setNewValue({
                          field: "git_url",
                          value: value.git_url,
                      })
                : undefined
        );
    },
});
export const repositoryNameColumn: AdditionalColumn = (setNewValue) => ({
    label: "Git repository",
    key: "repository_name",
    shown: false,
    sortable: false,
    dto: (value: any) => {
        const content = value?.repository_name;
        return buildSimpleColumn(
            content,
            setNewValue
                ? () =>
                      setNewValue({
                          field: "repository_name",
                          value: value.repository_name,
                      })
                : undefined
        );
    },
});
export const resourcesNumberColumn: AdditionalColumn = () => ({
    label: "Number of resources",
    key: "resource_count",
    shown: false,
    sortable: false,
    dto: (value) => {
        const type = value?.record_type?.toLowerCase();
        const numberOfResources = value?.[`resource_list_${type}_id`]?.resource_count;
        return numberOfResources;
    },
});
export const bundleRepositoryColumn: AdditionalColumn = (setNewValue) => ({
    label: "Bundle repository",
    key: "bundle_repository_name",
    shown: false,
    sortable: false,
    dto: (value) => {
        if (value?.bundle_repository_id_bundle_repository) {
            const { id, name } = value.bundle_repository_id_bundle_repository;
            const link = id ? getRelativeUrl("bundle_repository", id) : null;

            const content = <RecordLabel key={id} link={link} name={name} />;
            return buildSimpleColumn(
                content,
                setNewValue
                    ? () =>
                          setNewValue({
                              field: "bundle_repository_id_bundle_repository.name",
                              value: name,
                          })
                    : undefined
            );
        }
        return null;
    },
});
export const bundleVersionColumn: AdditionalColumn = (setNewValue) => ({
    label: "Version",
    key: "version",
    shown: true,
    sortable: false,
    dto: (value) =>
        buildSimpleColumn(
            value?.version,
            setNewValue
                ? () =>
                      setNewValue({
                          field: "version",
                          value: value.version,
                      })
                : undefined
        ),
});

const ExecutionPriority = ({ priority }: { priority?: number }) => {
    const defaultPriorityValue = useWorkspaceConfigValue("EXECUTION_DEFAULT_PRIORITY");
    return priority ?? defaultPriorityValue ?? null;
};

export const executionPriorityColumn: AdditionalColumn = (setNewValue) => ({
    label: "Execution priority",
    key: "priority",
    shown: true,
    sortable: true,
    dto: (value) => {
        const content = <ExecutionPriority priority={value?.priority} />;
        return buildSimpleColumn(
            content,
            setNewValue
                ? () => setNewValue({ field: "priority", value: value.priority })
                : undefined
        );
    },
});

export const isEnabledColumn: AdditionalColumn = (setNewValue) => ({
    label: "Enabled",
    key: "is_enabled",
    shown: true,
    sortable: true,
    dto: ({ is_enabled }: any) =>
        buildSimpleColumn(
            <SimpleToggle identifier="is_enabled" value={is_enabled} isReadOnly={true} />,
            setNewValue ? () => setNewValue({ field: "is_enabled", value: is_enabled }) : undefined
        ),
    filterOptions: {
        preset: "ternary",
    },
});

export const nextRunsColumn: AdditionalColumn = () => ({
    label: "Next runs",
    key: "next_runs",
    shown: true,
    sortable: false,
    dto: ({ next_runs, id }: Schedule) =>
        next_runs?.length ? (
            <ResizeableCodeEditor
                fieldProps={{
                    lang: "yaml",
                    isReadOnly: true,
                    valueTransformer: serializeToPrettyYaml,
                    initialHeight: 100,
                    minHeight: 100,
                }}
                input={{
                    name: `${id}-next_runs`,
                    value: next_runs,
                    onBlur: () => {},
                    onChange: () => {},
                    onFocus: () => {},
                }}
                fieldAttr={{}}
                meta={{}}
            />
        ) : null,
});

export const typeColumn = (recordType, setNewValue?: (data) => void) => ({
    label: "Type",
    key: `${recordType}_type`,
    shown: true,
    sortable: true,
    dto: ({ resource_type }) => {
        if (!resource_type) {
            return null;
        }
        const type = resource_type.toLowerCase().replace("_", " ");
        const resourceType = type.charAt(0).toUpperCase() + type.slice(1);
        return setNewValue ? (
            <ContextMenuWrapper
                handleClick={() =>
                    setNewValue({
                        field: "resource_type",
                        value: resource_type?.toUpperCase(),
                    })
                }
            >
                {resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}
            </ContextMenuWrapper>
        ) : (
            <div className="nameCell">
                <RecordLabel name={resourceType} />
            </div>
        );
    },
});

export const trackedInGitColumn: AdditionalColumn = (setNewValue?: (data) => void) => ({
    label: "Tracked in git",
    key: "track_in_git",
    shown: false,
    sortable: true,
    dto: (value: any) => {
        if (value?.track_in_git == null) {
            return null;
        } else {
            return buildSimpleColumn(
                <SimpleToggle
                    identifier="track_in_git"
                    value={value.track_in_git}
                    isReadOnly={true}
                />,
                setNewValue
                    ? () => setNewValue({ field: "track_in_git", value: value.track_in_git })
                    : undefined
            );
        }
    },
});
